// Killer's UI wiring. Unlike every other game here it has two screens before
// the match is actually decided - the shared mount() container starts on the
// number-assignment screen (each player throws in turn to claim a number, no
// duplicates, with its own undo), then swaps its own innerHTML over to the
// live board once everyone has one. Both screens share the same full-board
// dartboard input (components/dartboard-input.js's mountDartboardFullBoard),
// remounted fresh after every tap so its click handlers and claimed/dimmed
// wedges always match current state exactly - the same "just re-render"
// approach the rest of this controller uses instead of patching the DOM.
//
// Rules (the standard/original Killer, as opposed to Cricket-style Killer's
// own variant): everyone starts at 0 lives, and isn't a Killer. Hitting your
// own number on the required ring (double or triple, per `ring` - a setup
// choice, see definition.js's configRows) makes you a Killer and sets your
// lives to the configured max - permanently; nothing can ever undo Killer
// status once gained, even elimination. From then on, every one of your
// darts on the required ring removes exactly one life from whoever owns
// that number - including your own, if you tap it - regardless of which
// ring multiplier the dart would otherwise have scored (a single/non-
// -required ring hit does nothing at all, whether you're a Killer yet or
// not). A non-Killer's darts never do anything except the one that makes
// them a Killer. A non-Killer only ever has 0 lives, so a single qualifying
// hit against them (0 -> -1) eliminates them; a Killer has to be brought
// all the way down to exactly 0 instead. Last player not eliminated wins.
import killer from './definition.js';
import { mountDartboardFullBoard, drawDartMark } from '../../components/dartboard-input.js';
import { configRowDefault } from '../shared/config-rows.js';
import { killerObserverBoard } from '../shared/killer-observer-board.js';

let currentPlayers = [];
let container = null;
let onChangeCallback = null;
let lives = configRowDefault(killer, 'killer-lives');
let ring = configRowDefault(killer, 'ring');
let includeBull = configRowDefault(killer, 'killer-include-bull');
let toastTimer = null;
let isObserver = false;
let currentStage = 'assign'; // 'assign' | 'board' - which screen is actually mounted right now

// --- number-assignment phase ---
let assignments = {}; // player -> number, only for players who've picked so far
let assignIndex = 0;
let assignHistory = []; // stack of {player, number}, for undo

// --- live-board phase ---
let state = {}; // player -> { number, remaining, killer, eliminated }
let activeIndex = 0;
let dartsThisTurn = 0;
let turnDarts = []; // {number, mult} per dart thrown so far this turn, for the recent-darts row
let lastTurnDarts = []; // previous turn's darts, kept in the recent-darts row until the next player's first dart lands, instead of vanishing the instant the 3rd dart lands
let matchComplete = false;
let history = []; // stack of full-state snapshots, for undo
let renderedRemaining = {}; // player -> `remaining` as of the last renderAll(), for the life-dot pulse animation
// The most recent toast, carried in getState() so every synced client (an
// observer especially - it never runs handleGameDart/showToast itself) sees
// the same "X becomes a Killer!", "X eliminates Y!", etc. feedback, not just
// the client that threw the dart. `seq` is how setState() tells a genuinely
// new toast apart from the same one arriving again.
let toastSeq = 0;
let lastToast = null; // { msg, kind, seq } | null
let lastAppliedToastSeq = 0; // highest seq this client has already shown

function hasProgress() {
    return assignHistory.length > 0 || history.length > 0;
}

function numberLabel(num, short = false) {
    return num === 25 ? (short ? 'B' : 'Bull') : String(num);
}

// The required ring as a scoring multiplier - 2 for double, 3 for triple.
function requiredMult() {
    return ring === 'triple' ? 3 : 2;
}

// The bull has no true double/triple of its own (mountDartboardFullBoard
// only ever reports mult 1 for the outer bull or mult 2 for the bullseye),
// so it's mapped onto the two ring configs by relative difficulty instead
// of by a literal multiplier match: the easier outer bull stands in for
// Doubles, the harder bullseye for Triples - mirroring the house rule most
// tables actually play, rather than leaving the bull unable to ever
// qualify under a Triples game.
function qualifies(number, mult) {
    if (number === 25) return ring === 'triple' ? mult === 2 : mult === 1;
    return mult === requiredMult();
}

// The recent-darts row uses the plain number even for bull (25/D25), rather
// than numberLabel()'s "Bull" wording used elsewhere on the board.
function dartThrowLabel(number, mult) {
    const prefix = mult === 2 ? 'D' : mult === 3 ? 'T' : '';
    return `${prefix}${number}`;
}

function showToast(msg, kind) {
    toastSeq += 1;
    lastToast = { msg, kind, seq: toastSeq };
    lastAppliedToastSeq = toastSeq; // showing it ourselves right now - nothing for setState() to replay later
    renderToast(msg, kind);
}

// The actual DOM update, split out from showToast() so a toast that arrived
// via setState() (someone else's dart, replayed for this client) can be
// rendered the same way without re-triggering toastSeq/lastToast bookkeeping
// meant for this client's own actions.
function renderToast(msg, kind) {
    const el = document.getElementById('killerToast');
    if (!el) return;
    el.textContent = msg;
    el.className = 'killer-toast show ' + (kind || '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 2000);
}

function hideToast() {
    const el = document.getElementById('killerToast');
    if (el) el.classList.remove('show');
    clearTimeout(toastTimer);
}

// Dims and disables whichever wedges are already claimed - the same
// dim-everything-but-what's-relevant technique mountDartboardForTarget uses,
// just applied to a caller-supplied set of numbers on the full board instead
// of "everything except one target".
function dimClaimedNumbers(mount, claimedNumbers) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    claimedNumbers.forEach(num => {
        if (num === 25) {
            ['.outer-bull', '.bullseye'].forEach(sel => {
                const el = svg.querySelector(sel);
                if (el) { el.style.opacity = '0.3'; el.style.pointerEvents = 'none'; }
            });
            return;
        }
        const section = svg.querySelector(`g.section-${num}`);
        if (section) { section.style.opacity = '0.3'; section.style.pointerEvents = 'none'; }
        svg.querySelectorAll('text').forEach(label => {
            if (label.textContent.trim() === String(num)) label.style.opacity = '0.3';
        });
    });
}

// A real dartboard's wedges, in clockwise order starting from the top -
// dartboard.svg's own .section-divider lines are authored in this exact
// order too (divider i sits between WEDGE_ORDER[i-1] and WEDGE_ORDER[i]),
// which is what lets dimUnassignedNumbers below match each divider to the
// two numbers it separates without any per-divider geometry of its own.
const WEDGE_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

// Numbers nobody owns any more are fully dimmed - irrelevant to throw at.
// Numbers still owned by a player left in the game are only half-dimmed
// (opacity midway between this and full brightness) since one of them is
// this turn's actual target - highlightTargetNumbers below pulses that
// one ring on top of this dim to call it out. Left clickable (unlike
// dimClaimedNumbers above) since tapping a fully-dimmed one still means
// something - "No one owns it" - just a harmless one.
const FULL_DIM = '0.3';
const HALF_DIM = '0.65'; // (1 + FULL_DIM) / 2

function dimUnassignedNumbers(mount, assignedNumbers) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const assigned = new Set(assignedNumbers);
    // Dimmed per-path, not on the section <g> itself - an SVG group's own
    // opacity composites its whole subtree as one buffer, which would cap
    // the pulsing target ring's brightness right along with everything
    // else in its section instead of letting it stand out at full opacity.
    svg.querySelectorAll('g[class^="section-"]').forEach(section => {
        const match = /section-(\d+)/.exec(section.getAttribute('class') || '');
        if (!match) return;
        const opacity = assigned.has(Number(match[1])) ? HALF_DIM : FULL_DIM;
        section.querySelectorAll('path').forEach(path => { path.style.opacity = opacity; });
    });
    ['.outer-bull', '.bullseye'].forEach(sel => {
        const el = svg.querySelector(sel);
        if (el) el.style.opacity = assigned.has(25) ? HALF_DIM : FULL_DIM;
    });
    svg.querySelectorAll('text').forEach(label => {
        const num = Number(label.textContent.trim());
        if (!Number.isNaN(num)) label.style.opacity = assigned.has(num) ? HALF_DIM : FULL_DIM;
    });

    // A divider between two fully-dimmed (unassigned) wedges is just as
    // irrelevant as they are - only half-dim one if at least one of its
    // two neighbors is actually in play.
    const dividers = svg.querySelectorAll('.section-divider');
    if (dividers.length === WEDGE_ORDER.length) {
        dividers.forEach((line, i) => {
            const a = WEDGE_ORDER[(i - 1 + WEDGE_ORDER.length) % WEDGE_ORDER.length];
            const b = WEDGE_ORDER[i];
            line.style.opacity = (assigned.has(a) || assigned.has(b)) ? HALF_DIM : FULL_DIM;
        });
    }
}

// Pulses just the required ring (see `ring`/requiredMult()) of the
// number(s) the active player should go for - their own number while
// they're not yet a Killer (the only way to become one), or every alive
// opponent's number once they are one (hitting your own number as a Killer
// costs you a life, so it's deliberately never highlighted as a target) -
// only that one ring actually does anything here, unlike Cricket-style
// Killer where every ring counts. Black/red wedges (dartboard.svg pairs
// #2A2A2A singles with #CC0000 rings, same as the red .bullseye) start so
// much darker than their white/green counterparts that the same brightness
// multiplier barely reads on them - killer-target-wedge-dark pulses harder
// to look equally lit (see styles.css).
function highlightTargetNumbers(mount, targetNumbers) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const targets = new Set(targetNumbers);
    const ringClass = ring === 'triple' ? 'triple-ring' : 'double-ring';
    svg.querySelectorAll('g[class^="section-"]').forEach(section => {
        const match = /section-(\d+)/.exec(section.getAttribute('class') || '');
        if (!match) return;
        const isTarget = targets.has(Number(match[1]));
        const isDark = section.querySelector('.single')?.getAttribute('fill') === '#2A2A2A';
        const ringEl = section.querySelector(`.${ringClass}`);
        if (ringEl) {
            ringEl.classList.toggle('killer-target-wedge', isTarget && !isDark);
            ringEl.classList.toggle('killer-target-wedge-dark', isTarget && isDark);
            // The pulsing ring itself is never dimmed, even though
            // dimUnassignedNumbers (called just before this) half-dims the
            // rest of its own section.
            if (isTarget) ringEl.style.opacity = '';
        }
    });
    // The bull is mapped onto the two ring configs by relative difficulty
    // (see qualifies()) rather than a literal double/triple - the green
    // outer bull pulses under Doubles (green pairs with white/light wedges,
    // like .killer-target-wedge), the red bullseye under Triples (red
    // pairs with black/dark wedges, like .killer-target-wedge-dark).
    const bullSel = ring === 'triple' ? '.bullseye' : '.outer-bull';
    const bullClass = ring === 'triple' ? 'killer-target-wedge-dark' : 'killer-target-wedge';
    const bullEl = svg.querySelector(bullSel);
    if (bullEl) {
        bullEl.classList.toggle(bullClass, targets.has(25));
        if (targets.has(25)) bullEl.style.opacity = '';
    }
}

// --- number-assignment phase ---

function assignScreenHTML(players, observer) {
    // An observer sees the same dartboard (dimmed/undimmed exactly the same
    // way, read-only - see renderAssignScreen) but never gets the Undo/Start
    // buttons - there's nothing for it to drive there.
    return `
        <div class="killer-assign">
            <div class="killer-assign-header">
                <div class="killer-step">Choosing numbers &middot; player <b id="killerAssignStepNum">1</b> of <b>${players.length}</b></div>
                <h2 class="killer-turn-title"><b id="killerAssignPlayerName"></b></h2>
                <p class="killer-turn-hint" id="killerAssignHint">Tap a wedge on the board to claim that number</p>
            </div>
            <div class="killer-claimed-strip" id="killerClaimedStrip"></div>
            <div class="killer-assign-board-area">
                <div class="dartboard-mount killer-dart-mount" id="killerAssignDartMount"></div>
            </div>
            ${observer ? '' : `
            <div class="killer-assign-footer">
                <button class="grid-icon-btn grid-icon-btn-killer" type="button" id="killerAssignUndoBtn" disabled title="Undo last pick" aria-label="Undo last pick">
                    <i class="fa-solid fa-rotate-left"></i>
                </button>
                <button class="btn btn-large" type="button" id="killerAssignStartBtn" disabled>Start Game</button>
            </div>`}
        </div>
    `;
}

function notifyAssignChange() {
    if (!onChangeCallback) return;
    onChangeCallback({ scores: {}, complete: false });
}

function handleAssignTap(number) {
    if (assignIndex >= currentPlayers.length) return;
    if (number === 25 && includeBull !== 'yes') return; // bull excluded from this game - the dimmed wedge shouldn't fire anyway
    if (Object.values(assignments).includes(number)) return; // already claimed - the dimmed wedge shouldn't fire anyway

    const player = currentPlayers[assignIndex];
    assignments[player] = number;
    assignHistory.push({ player, number });
    assignIndex += 1;
    renderAssignScreen();
    notifyAssignChange();
}

function handleAssignUndo() {
    const last = assignHistory.pop();
    if (!last) return;
    delete assignments[last.player];
    assignIndex -= 1;
    renderAssignScreen();
    notifyAssignChange();
}

function renderAssignScreen() {
    const allAssigned = assignIndex >= currentPlayers.length;

    document.getElementById('killerAssignStepNum').textContent = Math.min(assignIndex + 1, currentPlayers.length);
    document.getElementById('killerAssignPlayerName').textContent = allAssigned
        ? 'Everyone has a number'
        : `${currentPlayers[assignIndex]}'s throw`;
    document.getElementById('killerAssignHint').textContent = allAssigned
        ? (isObserver ? '' : 'Press Start to begin the game')
        : (isObserver ? '' : 'Tap a wedge on the board to claim that number');

    document.getElementById('killerClaimedStrip').innerHTML = currentPlayers.map(player => {
        const num = assignments[player];
        const pending = num === undefined;
        return `
            <div class="killer-chip${pending ? ' pending' : ''}">
                <span class="killer-chip-num">${pending ? '?' : numberLabel(num, true)}</span>
                <span class="killer-chip-name">${player}</span>
            </div>
        `;
    }).join('');

    if (!isObserver) {
        document.getElementById('killerAssignUndoBtn').disabled = assignHistory.length === 0;
        document.getElementById('killerAssignStartBtn').disabled = !allAssigned;
    }

    const mount = document.getElementById('killerAssignDartMount');
    if (allAssigned) {
        mount.innerHTML = '';
        return;
    }
    mountDartboardFullBoard(mount, { onTap: (number) => handleAssignTap(number), readOnly: isObserver });
    // The bull is excluded from selection entirely when this game's
    // "Include Bull/Bullseye" config is No - dimmed and disabled exactly
    // like an already-claimed number, permanently for the whole assign
    // phase rather than just until someone picks it.
    const claimedForDim = includeBull === 'yes' ? Object.values(assignments) : [...Object.values(assignments), 25];
    dimClaimedNumbers(mount, claimedForDim);
}

// --- live-board phase ---

function boardScreenHTML(players, observer) {
    // The observer's wall display is a different layout altogether (see
    // renderObserverBoard), not the phone board minus its Undo button - it
    // only keeps the toast, which still carries "X eliminates Y!" feedback.
    if (observer) {
        return `
            <div class="obs-board obs-killer" id="killerObsBoard"></div>
            <div class="killer-toast" id="killerToast"></div>
        `;
    }
    const cards = players.map((player, i) => `
        <div class="killer-player" id="killer-player-${i}">
            <i class="fa-regular fa-circle-dot killer-turn-icon" id="killer-badge-${i}" style="visibility:hidden"></i>
            <div class="killer-number" id="killer-number-${i}"></div>
            <div class="killer-main">
                <div class="killer-name-row">
                    <span class="killer-name">${player}</span>
                    <span class="killer-badge" id="killer-killerbadge-${i}" style="display:none">Killer</span>
                    <span class="killer-out-badge" id="killer-outbadge-${i}" style="display:none">Out</span>
                </div>
                <div class="killer-lives" id="killer-lives-${i}"></div>
            </div>
        </div>
    `).join('');

    return `
        <div class="killer-board">
            <div class="killer-grid${players.length === 2 ? ' killer-grid-full-width' : ''}">${cards}</div>
            <div class="killer-board-header-row">
                <button class="grid-icon-btn-killer-pill killer-corner-undo" type="button" id="killerBoardUndoBtn" disabled title="Undo last dart" aria-label="Undo last dart">
                    <i class="fa-solid fa-rotate-left"></i> Undo
                </button>
                <div class="killer-board-header">
                    <div class="killer-turn-row" id="killerTurnRow"></div>
                </div>
            </div>
            <div class="killer-hint" id="killerHint"></div>
            <div class="dartboard-mount killer-dart-mount" id="killerBoardDartMount"></div>
            <div class="killer-recent-darts" id="killerRecentDarts"></div>
        </div>
        <div class="killer-toast" id="killerToast"></div>
    `;
}

// `prevRemaining` is the value this same row last rendered with - any dot
// whose filled/lost state differs between the two gets a one-shot pulse
// animation (see .killer-life-changed), so only the dots that actually
// gained or lost a life animate, not the whole row.
function livesDotsHTML(remaining, prevRemaining) {
    const lo = Math.max(0, Math.min(remaining, prevRemaining));
    const hi = Math.min(lives, Math.max(remaining, prevRemaining));
    let html = '';
    for (let i = 0; i < lives; i++) {
        const lost = i >= remaining;
        const changed = i >= lo && i < hi;
        html += `<span class="killer-life${lost ? ' lost' : ''}${changed ? ' killer-life-changed' : ''}"></span>`;
    }
    return html;
}

function ownerOf(number) {
    return currentPlayers.find(p => state[p].number === number) || null;
}

function isKiller(player) {
    return state[player].killer;
}

function checkWin() {
    const alive = currentPlayers.filter(p => !state[p].eliminated);
    if (alive.length <= 1) {
        matchComplete = true;
        if (alive.length === 1) showToast(`${alive[0]} wins!`, 'win');
    }
}

function advanceTurn() {
    // Kept around so the recent-darts row still shows the turn that just
    // ended - renderAll() only falls back to it while the new turn hasn't
    // thrown a dart of its own yet (turnDarts.length === 0), so it's
    // naturally superseded the moment the next player's first dart lands.
    lastTurnDarts = turnDarts;
    dartsThisTurn = 0;
    turnDarts = [];
    for (let i = 0; i < currentPlayers.length; i++) {
        activeIndex = (activeIndex + 1) % currentPlayers.length;
        if (!state[currentPlayers[activeIndex]].eliminated) break;
    }
}

function snapshotGame() {
    return JSON.parse(JSON.stringify({ state, activeIndex, dartsThisTurn, turnDarts, matchComplete }));
}

// The whole rules engine. A dart that isn't on the required ring never does
// anything, for anyone. Before you're a Killer, the only dart that matters
// at all is your own number on that ring - it makes you a Killer, at full
// (configured) lives, permanently. Once you are one, every one of your
// qualifying darts removes exactly one life from whoever owns that number
// (including yourself) - a non-Killer only ever has 0 lives, so a single
// such hit (0 -> -1) is enough to eliminate them; a Killer has to be
// brought all the way down to exactly 0 instead of below it.
// Bull has no literal double/triple (see qualifies()), so the required-ring
// wording has to follow the same outer-bull/bullseye mapping rather than
// always saying "double"/"triple" - otherwise a Killer assigned the bull
// gets told to hit a "double" or "triple" on a number that doesn't have one.
function ringWordFor(number) {
    if (number === 25) return ring === 'triple' ? 'bullseye' : 'single bull';
    return ring === 'triple' ? 'triple' : 'double';
}

function applyThrow(thrower, number, mult) {
    const throwerState = state[thrower];

    if (!throwerState.killer) {
        if (number === throwerState.number && qualifies(number, mult)) {
            throwerState.killer = true;
            throwerState.remaining = lives;
            showToast(`${thrower} becomes a Killer!`, 'killer');
        } else if (number === throwerState.number) {
            const target = throwerState.number === 25
                ? ringWordFor(throwerState.number)
                : `${ringWordFor(throwerState.number)} on their own number`;
            showToast(`${thrower} needs a ${target} to become a Killer`, '');
        } else {
            showToast(`${thrower} isn't a Killer yet - only their own number does anything`, '');
        }
        return;
    }

    if (!qualifies(number, mult)) {
        showToast(`${thrower} needs a ${ringWordFor(number)} to deal damage`, '');
        return;
    }

    const owner = ownerOf(number);
    if (!owner) {
        showToast(`No one owns ${numberLabel(number)}`, '');
        return;
    }
    if (state[owner].eliminated) {
        // Already out - nothing left to hit, and re-applying damage to an
        // already-eliminated `remaining` would keep re-triggering the
        // eliminates-them toast on every later hit, not just the first.
        showToast(`${owner} is already eliminated`, '');
        return;
    }

    const ownerState = state[owner];
    const ownNumber = owner === thrower;
    ownerState.remaining -= 1;
    const eliminated = ownerState.killer ? ownerState.remaining <= 0 : ownerState.remaining < 0;
    if (eliminated) ownerState.eliminated = true;

    showToast(
        eliminated
            ? (ownNumber ? `${thrower} hits their own number and is eliminated!` : `${thrower} eliminates ${owner}!`)
            : (ownNumber
                ? `${thrower} hits their own number — loses a life (${ownerState.remaining}/${lives})`
                : `${thrower} hits ${owner}'s number — a life lost (${ownerState.remaining}/${lives})`),
        'bust'
    );
}

function handleGameDart(number, mult, point) {
    if (matchComplete) return;
    const activePlayer = currentPlayers[activeIndex];
    history.push(snapshotGame());

    applyThrow(activePlayer, number, mult);

    turnDarts.push({ number, mult, x: point && point.x, y: point && point.y });
    dartsThisTurn += 1;
    checkWin();
    // A self-hit that eliminates the active player (see applyThrow) ends
    // their turn on the spot, same as running out of darts - there's no
    // reason for someone who just took themselves out to keep throwing.
    if (!matchComplete && (state[activePlayer].eliminated || dartsThisTurn >= 3)) advanceTurn();

    renderAll();
    notifyChange();
}

function undoLastDart() {
    const snap = history.pop();
    if (!snap) return;
    state = snap.state;
    activeIndex = snap.activeIndex;
    dartsThisTurn = snap.dartsThisTurn;
    turnDarts = snap.turnDarts;
    matchComplete = snap.matchComplete;
    // turnDarts is now restored from the snapshot, so lastTurnDarts (the
    // prior turn's darts) is no longer relevant - drop it so it can't
    // resurface if this restored turn is itself later undone back to empty.
    lastTurnDarts = [];
    hideToast();
    renderAll();
    // Undo must never be what finishes the match - it's a correction, not a
    // dart (matchComplete is always false in a restored snapshot anyway,
    // since one can't be pushed after the match already ended).
    notifyChange({ suppressComplete: true });
}

// What a dart must hit to count against `number`, in the short form the
// observer board prints (D6, T6, or Bull/Bullseye - see qualifies() for the
// bull mapping).
function killLabel(number) {
    if (number === 25) return ring === 'triple' ? 'Bullseye' : 'Bull';
    return `${ring === 'triple' ? 'T' : 'D'}${number}`;
}

// The observer's wall display (a tablet a few metres from the players), laid
// out by games/shared/killer-observer-board.js. Rebuilt wholesale on every
// render - a handful of rows is cheap, and rebuilding is what replays the
// life-dot pulse (see livesDotsHTML) on just the dots that changed.
function renderObserverBoard() {
    const board = document.getElementById('killerObsBoard');
    if (!board) return;

    const active = currentPlayers[activeIndex];
    const killerActive = isKiller(active);
    const { html, classes } = killerObserverBoard({
        players: currentPlayers,
        state,
        activeIndex,
        matchComplete,
        title: 'Killer',
        subtitle: `${lives} lives &middot; ${ring}s${includeBull === 'yes' ? ' + bull' : ''}`,
        isKiller,
        numberLabel: number => numberLabel(number, true),
        targetsHeading: killerActive ? 'Kill with' : 'Become a Killer with',
        targetLabels: killerActive
            ? currentPlayers.filter(p => p !== active && !state[p].eliminated).map(p => killLabel(state[p].number))
            : [killLabel(state[active].number)],
        dartLabels: (turnDarts.length ? turnDarts : lastTurnDarts).map(({ number, mult }) => dartThrowLabel(number, mult)),
        noteFor: player => state[player].eliminated
            ? 'Eliminated'
            : state[player].killer
                ? `Lose a life to <b>${killLabel(state[player].number)}</b>`
                : `Needs <b>${killLabel(state[player].number)}</b> to become a Killer`,
        livesHTML: player => livesDotsHTML(state[player].remaining, renderedRemaining[player] ?? 0)
    });
    Object.entries(classes).forEach(([name, on]) => board.classList.toggle(name, on));
    board.innerHTML = html;
    currentPlayers.forEach(player => { renderedRemaining[player] = state[player].remaining; });
}

function renderAll() {
    if (isObserver) {
        renderObserverBoard();
        return;
    }
    currentPlayers.forEach((player, i) => {
        const p = state[player];
        const isTurn = i === activeIndex && !matchComplete;

        const numEl = document.getElementById(`killer-number-${i}`);
        if (numEl) numEl.textContent = numberLabel(p.number, true);

        const card = document.getElementById(`killer-player-${i}`);
        if (card) {
            card.classList.toggle('active', isTurn);
            card.classList.toggle('out', p.eliminated);
        }

        // Killer status is shown on the number circle's color and the
        // badge below - not the whole card border, which would otherwise
        // compete with the active-turn border for attention.
        if (numEl) numEl.classList.toggle('killer', p.killer);

        const badge = document.getElementById(`killer-badge-${i}`);
        if (badge) badge.style.visibility = isTurn ? 'visible' : 'hidden';

        const killerBadge = document.getElementById(`killer-killerbadge-${i}`);
        if (killerBadge) killerBadge.style.display = p.killer ? 'inline-block' : 'none';

        const outBadge = document.getElementById(`killer-outbadge-${i}`);
        if (outBadge) outBadge.style.display = p.eliminated ? 'inline-block' : 'none';

        const livesEl = document.getElementById(`killer-lives-${i}`);
        if (livesEl) livesEl.innerHTML = livesDotsHTML(p.remaining, renderedRemaining[player] ?? 0);
        renderedRemaining[player] = p.remaining;
    });

    const undoBtn = document.getElementById('killerBoardUndoBtn');
    if (undoBtn) undoBtn.disabled = history.length === 0;

    const turnRow = document.getElementById('killerTurnRow');
    if (turnRow) {
        turnRow.innerHTML = matchComplete
            ? 'Game over'
            : `<b>${currentPlayers[activeIndex]}</b>'s throw &middot; dart <b>${dartsThisTurn + 1}</b> of 3`;
    }

    const hint = document.getElementById('killerHint');
    if (hint) hint.textContent = matchComplete ? '' : (isObserver ? '' : 'Tap the dartboard for this throw');

    const dartMount = document.getElementById('killerBoardDartMount');
    if (dartMount) {
        if (matchComplete) {
            dartMount.innerHTML = '';
        } else {
            // Remounted fresh on every dart (see file-top comment), which
            // wipes any dot drawn on the previous mount - replaying every
            // dart already thrown this turn (reset on undo/a new turn along
            // with turnDarts itself) puts them all back immediately.
            mountDartboardFullBoard(dartMount, { onTap: handleGameDart, readOnly: isObserver });
            dimUnassignedNumbers(dartMount, currentPlayers.filter(p => !state[p].eliminated).map(p => state[p].number));
            const activePlayer = currentPlayers[activeIndex];
            const targetNumbers = isKiller(activePlayer)
                ? currentPlayers.filter(p => p !== activePlayer && !state[p].eliminated).map(p => state[p].number)
                : [state[activePlayer].number];
            highlightTargetNumbers(dartMount, targetNumbers);
            turnDarts.forEach(({ x, y }) => {
                if (x !== undefined) drawDartMark(dartMount, x, y);
            });
        }
    }

    const recentDarts = document.getElementById('killerRecentDarts');
    if (recentDarts) {
        const dartsToShow = turnDarts.length ? turnDarts : lastTurnDarts;
        recentDarts.innerHTML = dartsToShow
            .map(({ number, mult }) => `<span>${dartThrowLabel(number, mult)}</span>`)
            .join(' - ');
    }
}

function notifyChange({ suppressComplete = false } = {}) {
    if (!onChangeCallback) return;
    const scores = {};
    const standingLabels = {};
    currentPlayers.forEach(player => {
        scores[player] = state[player].remaining;
        standingLabels[player] = isKiller(player) ? 'Killer' : '';
    });
    onChangeCallback({ scores, complete: !suppressComplete && matchComplete, standingLabels });
}

function beginGamePhase() {
    state = {};
    currentPlayers.forEach(player => {
        state[player] = { number: assignments[player], remaining: 0, killer: false, eliminated: false };
    });
    activeIndex = 0;
    dartsThisTurn = 0;
    turnDarts = [];
    lastTurnDarts = [];
    matchComplete = false;
    history = [];
    renderedRemaining = {};
    currentStage = 'board';
    toastSeq = 0;
    lastToast = null;
    lastAppliedToastSeq = 0;

    container.innerHTML = boardScreenHTML(currentPlayers, isObserver);
    if (!isObserver) document.getElementById('killerBoardUndoBtn').addEventListener('click', undoLastDart);
    renderAll();
    notifyChange();
}

function getState() {
    return JSON.parse(JSON.stringify(
        currentStage === 'assign'
            ? { stage: 'assign', assignments, assignHistory, assignIndex }
            : { stage: 'board', state, activeIndex, dartsThisTurn, turnDarts, lastTurnDarts, matchComplete, lastToast }
    ));
}

function setState(snapshot) {
    if (snapshot.stage === 'assign') {
        assignments = snapshot.assignments;
        assignHistory = snapshot.assignHistory;
        assignIndex = snapshot.assignIndex;
        if (currentStage !== 'assign') {
            currentStage = 'assign';
            container.innerHTML = assignScreenHTML(currentPlayers, isObserver);
            if (!isObserver) {
                document.getElementById('killerAssignUndoBtn').addEventListener('click', handleAssignUndo);
                document.getElementById('killerAssignStartBtn').addEventListener('click', beginGamePhase);
            }
        }
        renderAssignScreen();
        return;
    }

    if (currentStage !== 'board') {
        currentStage = 'board';
        container.innerHTML = boardScreenHTML(currentPlayers, isObserver);
        if (!isObserver) document.getElementById('killerBoardUndoBtn').addEventListener('click', undoLastDart);
        // Baseline the pulse-animation tracker at the incoming values rather
        // than wiping it to {} - joining mid-match, already-lost lives
        // shouldn't animate in as if just lost on this first paint. Only
        // done on this first arrival at the board stage - every later
        // setState() call in this same match leaves renderedRemaining alone
        // so it keeps comparing against the truly-last-rendered values (see
        // livesDotsHTML) instead of resetting the comparison to 0 on every
        // single sync update, which animated every dot on every update.
        renderedRemaining = {};
        currentPlayers.forEach(p => { renderedRemaining[p] = snapshot.state[p].remaining; });
    }
    state = snapshot.state;
    activeIndex = snapshot.activeIndex;
    dartsThisTurn = snapshot.dartsThisTurn;
    turnDarts = snapshot.turnDarts;
    lastTurnDarts = snapshot.lastTurnDarts;
    matchComplete = snapshot.matchComplete;
    if (snapshot.lastToast && snapshot.lastToast.seq !== lastAppliedToastSeq) {
        lastAppliedToastSeq = snapshot.lastToast.seq;
        renderToast(snapshot.lastToast.msg, snapshot.lastToast.kind);
    } else {
        hideToast();
    }
    renderAll();
    // See x01-game-controller.js's setState for why this matters - a client
    // that only receives a finished match over sync still needs this to
    // reach app.js's handleGameChange and arm the victory modal.
    notifyChange();
}

function mount(container_, { players, gameId, onChange, lives: livesOption, ring: ringOption, includeBull: includeBullOption, observer = false }) {
    container = container_;
    currentPlayers = players;
    onChangeCallback = onChange;
    lives = livesOption || configRowDefault(killer, 'killer-lives');
    ring = ringOption || configRowDefault(killer, 'ring');
    includeBull = includeBullOption || configRowDefault(killer, 'killer-include-bull');
    isObserver = observer;
    currentStage = 'assign';
    toastSeq = 0;
    lastToast = null;
    lastAppliedToastSeq = 0;

    assignments = {};
    assignHistory = [];
    assignIndex = 0;

    container.innerHTML = assignScreenHTML(players, isObserver);
    if (!isObserver) {
        document.getElementById('killerAssignUndoBtn').addEventListener('click', handleAssignUndo);
        document.getElementById('killerAssignStartBtn').addEventListener('click', beginGamePhase);
    }
    renderAssignScreen();
}

export default { definition: killer, mount, hasProgress, getState, setState };
